import { z } from 'zod';

/**
 * Email validation schema
 */
export const emailSchema = z.string().min(1, 'Email is required').email('Invalid email address');

/**
 * Password validation schema with configurable requirements
 */
export function createPasswordSchema(options?: {
  minLength?: number;
  requireUppercase?: boolean;
  requireLowercase?: boolean;
  requireNumber?: boolean;
  requireSpecialChar?: boolean;
}) {
  const {
    minLength = 6,
    requireUppercase = false,
    requireLowercase = false,
    requireNumber = false,
    requireSpecialChar = false,
  } = options || {};

  let schema = z.string().min(minLength, `Password must be at least ${minLength} characters`);

  if (requireUppercase) {
    schema = schema.regex(/[A-Z]/, 'Password must contain at least one uppercase letter');
  }

  if (requireLowercase) {
    schema = schema.regex(/[a-z]/, 'Password must contain at least one lowercase letter');
  }

  if (requireNumber) {
    schema = schema.regex(/\d/, 'Password must contain at least one number');
  }

  if (requireSpecialChar) {
    schema = schema.regex(
      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
      'Password must contain at least one special character'
    );
  }

  return schema;
}

/**
 * Default password schema (minimum 6 characters)
 */
export const passwordSchema = createPasswordSchema();

/**
 * Validate email format
 */
export function validateEmail(email: string): { valid: boolean; error?: string } {
  const result = emailSchema.safeParse(email);
  if (result.success) {
    return { valid: true };
  }
  return { valid: false, error: result.error.message };
}

/**
 * Validate password format
 */
export function validatePassword(
  password: string,
  options?: Parameters<typeof createPasswordSchema>[0]
): { valid: boolean; error?: string } {
  const schema = createPasswordSchema(options);
  const result = schema.safeParse(password);
  if (result.success) {
    return { valid: true };
  }
  return { valid: false, error: result.error.message };
}

/**
 * Validate password strength based on multiple criteria
 */
export function checkPasswordStrength(password: string): {
  score: number;
  feedback: string[];
} {
  const feedback: string[] = [];
  let score = 0;

  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push('Use at least 8 characters');
  }

  if (password.length >= 12) {
    score += 1;
  }

  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Use both uppercase and lowercase letters');
  }

  if (/\d/.test(password)) {
    score += 1;
  } else {
    feedback.push('Include at least one number');
  }

  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Include at least one special character');
  }

  return { score, feedback };
}
